import { useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { api } from '../api.ts'
import { useAuth } from '../auth/AuthContext.tsx'
import { money, percent, categoryLabel } from '../format.ts'
import { EditMachineDialog } from '../components/EditMachineDialog.tsx'
import { OptionsEditor } from '../components/OptionsEditor.tsx'

interface Option {
  id: string
  name: string
  price: string | null
  currency: string
  defaultSelected: boolean
  purchasePrice?: string | null
  optionGroup?: string | null
  variantModel?: string | null
}

interface MachineDetail {
  id: string
  offerTitle?: string | null
  model: string
  category: string
  catalogCode: string
  waproSku: string | null
  description: string | null
  techSpec: { key: string; value: string }[]
  retailPrice: string | null
  retailCurrency: string
  vatRate: string
  deliveryTime: string | null
  status: string
  manufacturerName: string
  options: Option[]
  // Owner-only; the API omits the whole object for sales.
  cost?: {
    listPrice: string
    listCurrency: string
    dealerDiscountPct: string
    purchasePrice: string | null
  } | null
}

export function MachinePage() {
  const { id } = useParams<{ id: string }>()
  const { user } = useAuth()
  const [m, setM] = useState<MachineDetail | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [editing, setEditing] = useState(false)
  const isOwner = user?.role === 'wlasciciel'

  function reload() {
    if (!id) return
    api
      .get<{ machine: MachineDetail }>(`/api/machines/${id}`)
      .then((r) => setM(r.machine))
      .catch((e) => setError(e instanceof Error ? e.message : 'Błąd'))
  }
  useEffect(reload, [id])

  if (error) return <div className="p-6 text-red-700">{error}</div>
  if (!m) return <div className="p-6 text-slate-400">Ładowanie…</div>

  return (
    <div className="p-6 max-w-4xl">
      <Link to="/katalog" className="text-sm text-slate-500 hover:text-slate-700">
        ← Katalog
      </Link>

      <div className="mt-2 mb-5 flex items-start justify-between gap-4">
        <div>
          <div className="text-sm text-slate-500">{m.manufacturerName}</div>
          <h1 className="text-xl font-bold text-slate-800">{m.model}</h1>
          <div className="flex gap-2 mt-1 text-xs">
            <span className="bg-slate-100 text-slate-600 px-2 py-0.5 rounded">{categoryLabel(m.category)}</span>
            {m.offerTitle && <span className="text-slate-500" title="Tytuł oferty — na co jest oferta na tę maszynę">oferta na: {m.offerTitle}</span>}
            <span className="font-mono bg-slate-100 text-slate-600 px-2 py-0.5 rounded">{m.catalogCode}</span>
            {m.status === 'archiwalna' && <span className="bg-amber-100 text-amber-700 px-2 py-0.5 rounded">archiwalna</span>}
          </div>
        </div>
        {isOwner && (
          <button
            onClick={() => setEditing(true)}
            className="shrink-0 text-sm border border-slate-300 text-slate-700 px-3 py-1.5 rounded-md hover:border-brand-600 hover:text-brand-700"
          >
            Edytuj
          </button>
        )}
      </div>

      {editing && isOwner && (
        <EditMachineDialog
          machine={m}
          onClose={() => setEditing(false)}
          onSaved={() => {
            setEditing(false)
            reload()
          }}
        />
      )}

      <div className="grid gap-4 md:grid-cols-2">
        {/* Prices */}
        <section className="bg-white border border-slate-200 rounded-lg p-4">
          <h2 className="font-semibold text-slate-700 mb-3 text-sm">Ceny</h2>
          <dl className="space-y-1.5 text-sm">
            <Row label="Cena detaliczna netto" value={money(m.retailPrice, m.retailCurrency)} strong />
            <Row label="VAT" value={percent(m.vatRate)} />
            {m.deliveryTime && <Row label="Termin dostawy" value={m.deliveryTime} />}

            {/* Owner-only block. Sales never receives `cost` from the API. */}
            {isOwner && m.cost && (
              <>
                <li className="pt-2 mt-2 border-t border-slate-100 text-xs uppercase text-slate-400">
                  Tylko właściciel
                </li>
                <Row label="Cena katalogowa producenta" value={money(m.cost.listPrice, m.cost.listCurrency)} />
                <Row label="Rabat dealerski" value={percent(m.cost.dealerDiscountPct)} />
                <Row label="Cena zakupu" value={money(m.cost.purchasePrice, m.cost.listCurrency)} strong />
              </>
            )}
          </dl>
        </section>

        {/* Tech spec */}
        <section className="bg-white border border-slate-200 rounded-lg p-4">
          <h2 className="font-semibold text-slate-700 mb-3 text-sm">Specyfikacja techniczna</h2>
          {m.techSpec.length ? (
            <dl className="space-y-1.5 text-sm">
              {m.techSpec.map((s, i) => (
                <Row key={i} label={s.key} value={s.value} />
              ))}
            </dl>
          ) : (
            <p className="text-sm text-slate-400">Brak danych.</p>
          )}
          {m.description && <p className="text-sm text-slate-600 mt-3 pt-3 border-t border-slate-100">{m.description}</p>}
        </section>
      </div>

      {/* Options: the owner edits them here, sales just reads the list. */}
      {isOwner ? (
        <OptionsEditor machineId={m.id} options={m.options} currency={m.retailCurrency} onChanged={reload} />
      ) : (
        <section className="bg-white border border-slate-200 rounded-lg p-4 mt-4">
          <h2 className="font-semibold text-slate-700 mb-3 text-sm">Konfiguracja i wyposażenie dodatkowe</h2>
          {m.options.length ? (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase text-slate-400 border-b border-slate-100">
                  <th className="pb-1 font-medium">Grupa</th>
                  <th className="pb-1 font-medium">Opcja</th>
                  <th className="pb-1 font-medium text-right">Cena netto</th>
                  <th className="pb-1 font-medium text-center">Domyślna</th>
                </tr>
              </thead>
              <tbody>
                {m.options.map((o) => (
                  <tr key={o.id} className="border-b border-slate-50 last:border-0">
                    <td className="py-1.5 text-xs text-slate-500">{o.optionGroup ?? '—'}</td>
                    <td className="py-1.5">
                      {o.name}
                      {o.variantModel && <span className="ml-2 text-xs text-slate-400">→ {o.variantModel}</span>}
                    </td>
                    <td className="py-1.5 text-right tabular">{money(o.price, o.currency)}</td>
                    <td className="py-1.5 text-center">{o.defaultSelected ? '✓' : ''}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <p className="text-sm text-slate-400">Brak wyposażenia dodatkowego.</p>
          )}
        </section>
      )}
    </div>
  )
}

function Row({ label, value, strong }: { label: string; value: string; strong?: boolean }) {
  return (
    <li className="flex justify-between gap-4 list-none">
      <dt className="text-slate-500">{label}</dt>
      <dd className={`tabular ${strong ? 'font-semibold text-slate-800' : 'text-slate-700'}`}>{value}</dd>
    </li>
  )
}
